import { clsx } from 'clsx';
import { useTranslation } from '@shared/i18n';
import { useSubscription } from '../hooks/useSubscription';
import type { SubscriptionStatus as Status } from '@tailorcast/shared-types';

const statusBadgeClass: Record<Status, string> = {
  active: 'bg-green-100 text-green-800',
  trial: 'bg-yellow-100 text-yellow-800',
  expired: 'bg-red-100 text-red-800',
};

const statusKey: Record<Status, string> = {
  active: 'subscription.status_active',
  trial: 'subscription.status_trial',
  expired: 'subscription.status_expired',
};

function UsageBar({ used, limit, label }: { used: number; limit: number | null; label: string }) {
  const { t } = useTranslation();

  if (limit === null) {
    return (
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-600">{label}</span>
        <span className="text-gray-500">{t('subscription.unlimited')}</span>
      </div>
    );
  }

  const pct = limit > 0 ? Math.min((used / limit) * 100, 100) : 0;
  const isHigh = pct >= 90;

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-600">{label}</span>
        <span className="text-gray-900 font-medium">
          {used} / {limit}
        </span>
      </div>
      <div className="bg-gray-200 rounded-full h-2">
        <div
          className={clsx('h-2 rounded-full', isHigh ? 'bg-red-500' : 'bg-blue-500')}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

export function SubscriptionStatus() {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useSubscription();

  if (isLoading) {
    return (
      <div className="card p-6">
        <p className="text-sm text-gray-500">{t('common.loading')}</p>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="card p-6">
        <p className="text-sm text-red-600">{t('common.error')}</p>
      </div>
    );
  }

  const showUpgrade = data.status === 'trial' || data.status === 'expired';

  return (
    <div className="card p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">{t('subscription.title')}</h3>

      <div className="flex items-center gap-2 mb-4">
        <span className="text-sm text-gray-600">{t('subscription.plan')}:</span>
        <span className="text-sm font-medium text-gray-900">{data.planName}</span>
        <span
          className={clsx(
            'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
            statusBadgeClass[data.status]
          )}
        >
          {t(statusKey[data.status])}
        </span>
      </div>

      <div className="space-y-3 mb-4">
        <UsageBar
          used={data.usage.products}
          limit={data.usage.productsLimit}
          label={t('subscription.products_usage')}
        />
        <UsageBar
          used={data.usage.tasks}
          limit={data.usage.tasksLimit}
          label={t('subscription.tasks_usage')}
        />
      </div>

      {data.expiresAt && (
        <p className="text-sm text-gray-500 mb-4">
          {t('subscription.expires')}: {new Date(data.expiresAt).toLocaleDateString()}
        </p>
      )}

      {showUpgrade && (
        <button
          onClick={() => window.BX24?.openPath('/marketplace/')}
          className="btn btn-primary text-sm"
        >
          {t('subscription.upgrade')}
        </button>
      )}
    </div>
  );
}
